/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ReactElement } from 'react'
import { createElement } from 'react'

import { extractContentIdFromUrl, shouldAddTracking } from '../utils/get-tracking-info'
import { componentMap } from './component-map'

export const renderArticleElements = (
  articleBody: any = [],
  additionalInfo?: {
    articleUrl?: string
    subVerticalSlug?: string
    context?: string
    title?: string
  }
): ReactElement =>
  articleBody.reduce((elements, currentElement) => {
    if (!currentElement?.contentTag && !currentElement?.tagName && !currentElement?.component) {
      if (!skipableElements.includes(currentElement?.name ?? '')) {
        console.error('ARTICLE RENDERER ERROR: Invalid article body json element:', {
          articleUrl: additionalInfo?.articleUrl ?? 'unknown',
          invalidElement: currentElement,
        })
      }

      return elements
    }

    if (currentElement.contentTag) {
      return elements.concat(renderArticleElements(currentElement.children, additionalInfo))
    }
    /**
     * Text type elements are broken chunks that form a paragraph.
     * These are usually returned if a <p> has nested elements e.g.
     * <p>chunk <a href="url"> chunk</p>
     */
    if (currentElement.tagName === 'text') {
      return elements.concat(currentElement.innerText)
    }

    if (currentElement.component) {
      const component = componentMap[currentElement.component]
      const componentRequiresRenderer = componentsWithRenderers.includes(currentElement.component)

      const combinedInnerText = currentElement.children
        ? currentElement.children.map((child: any) => child.innerText).join('')
        : ''

      if (
        currentElement.component === 'StandardLink' &&
        shouldAddTracking(additionalInfo?.subVerticalSlug) &&
        additionalInfo?.context === 'accordion' &&
        additionalInfo?.articleUrl?.includes(`/topic/`)
      ) {
        const trackingProps = {
          'data-which-id': `${additionalInfo.subVerticalSlug} link`,
          'data-section': additionalInfo.title,
          'data-card-name': combinedInnerText,
          'data-content-id': extractContentIdFromUrl(currentElement.props.href),
        }

        return elements.concat(
          createElement(
            component || 'a',
            component
              ? {
                  key: currentElement.props?.key || currentElement.id,
                  ...currentElement.props,
                  ...trackingProps,
                  ...(componentRequiresRenderer ? { renderArticleElements } : {}),
                }
              : { key: currentElement.props?.key || currentElement.id },
            calculateElementContent({
              element: currentElement,
              additionalInfo,
            })
          )
        )
      }

      return elements.concat(
        createElement(
          component || 'div',
          component
            ? {
                key: currentElement.props?.key || currentElement.id,
                ...currentElement.props,
                ...(componentRequiresRenderer ? { renderArticleElements } : {}),
              }
            : { key: currentElement.props?.key || currentElement.id },
          calculateElementContent({
            element: currentElement,
            additionalInfo,
          })
        )
      )
    }

    return elements.concat(
      createElement(
        currentElement.tagName,
        { ...currentElement.props, key: currentElement.props?.key || currentElement.id },
        calculateElementContent({
          element: currentElement,
          additionalInfo,
        })
      )
    )
  }, [])

const calculateElementContent = ({
  element,
  additionalInfo,
}: {
  element: any
  additionalInfo?: {
    articleUrl?: string
    subVerticalSlug?: string
    context?: string
    title?: string
  }
}): string | ReactElement | null => {
  const { innerText, children } = element

  if (innerText) {
    return innerText
  }

  if (children) {
    return renderArticleElements(children, additionalInfo)
  }

  return null
}

const componentsWithRenderers = ['CsvTable']

const skipableElements = ['logged-in-content', 'logged-out-content']
